import type { BaseTemplate } from '../types/baseTemplates';
import type { OutputFormat, Template } from '../wysiwyg/types';
import { parseTemplateForClone } from './templateClone';

const LEGACY_SOURCE_DPI = 72;

const physicalInches = (value: number, unit: string): number | null => {
  if (!Number.isFinite(value) || value <= 0) return null;
  const normalizedUnit = unit.trim().toLowerCase();
  if (normalizedUnit === 'in' || normalizedUnit === 'inch' || normalizedUnit === 'inches') return value;
  if (normalizedUnit === 'cm' || normalizedUnit === 'centimeter' || normalizedUnit === 'centimeters') {
    return value / 2.54;
  }
  return null;
};

export const inferBaseTemplateSourceDpi = (
  template: Pick<Template, 'dpi' | 'width' | 'height'>,
  source: Pick<BaseTemplate, 'width' | 'height' | 'unit'>,
): number => {
  if (Number.isFinite(template.dpi) && Number(template.dpi) > 0) return Number(template.dpi);

  const physicalWidth = physicalInches(source.width, source.unit);
  const physicalHeight = physicalInches(source.height, source.unit);
  const candidates = [
    physicalWidth ? template.width / physicalWidth : NaN,
    physicalHeight ? template.height / physicalHeight : NaN,
  ].filter((value) => Number.isFinite(value) && value > 0);

  if (!candidates.length) return LEGACY_SOURCE_DPI;
  return Math.round(candidates.reduce((total, value) => total + value, 0) / candidates.length);
};

export const prepareBaseTemplateSource = (source: BaseTemplate): Template | null => {
  const parsed = parseTemplateForClone(source.content || source.data);
  if (!parsed) return null;
  const dpi = inferBaseTemplateSourceDpi(parsed, source);
  const outputFormat: OutputFormat = parsed.outputFormat === 'pdf' || parsed.outputFormat === 'zpl'
    ? parsed.outputFormat
    : dpi === 203 || dpi === 300 ? 'zpl' : 'pdf';
  return { ...parsed, dpi, outputFormat };
};
